/**
 * Module 4: The Listener's Mindset
 *
 * In which Daria arrives frightened of a room, and Xenia asks her who is in it.
 * Primary character: Xenia | Supporting: Theron, Philemon | Guest: Daria
 *
 * REWORKED 2026-10-04 to the six rules Evening 1 set
 * (docs/CONTENT_REVIEW_2026-10-04.md).
 *
 *   What happens: three weeks before the gathering, Daria comes through the
 *   gate sure the room will judge her. The judge she fears most is Philemon,
 *   and she says so without knowing he is at the table. Xenia then has her
 *   describe one real listener, the eel-seller, with three questions.
 *
 *   Who is wrong: Theron. Some rooms are hostile, and being generous about
 *   them is how you get fleeced. He has stood in those rooms. Xenia asks him
 *   one question and lets him keep his answer; he ends the evening with his
 *   arms folded. Do not have him concede.
 *
 *   The one line that stands alone: Xenia's "Attention always has somewhere to
 *   live, Daria. If you don't give it a room, it will live in you." Nobody
 *   explains it afterwards.
 *
 *   The one technique: three questions about one real listener (what do they
 *   know, what do they need, what are they afraid of), and the kindest
 *   plausible explanation for a face. The reader meets it in the turn, in
 *   exercise 1, and in exercise 2 with the Audience Portrait.
 *
 * CONTINUITY this evening must keep (later evenings depend on it):
 *   - Three weeks to the gathering (Evening 3 announced it a month out;
 *     Evening 6 is one week out).
 *   - Daria's uncle put her name forward. She is to speak about the harbour
 *     levy (Evening 7: "Levy. Change. Grandmother."). Her grandmother and her
 *     seventeen points belong to Evening 7; neither is mentioned here.
 *   - Daria, word for word: "I'm going to write that on my other hand." Her
 *     first hand already says "slower". By Evening 7 both hands are written
 *     on to the wrist.
 *   - She arrives with the wax tablet held to her chest (Evening 7: "The wax
 *     tablet is back against her chest").
 *   - Philemon is to speak at the gathering too, and his hands still shake
 *     (Evening 1). He adds one detail to Evening 1's worst day: of the forty
 *     scholars, he could name two who enjoyed it.
 *   - Eudoros is alive tonight. Evening 5 opens with his death, so the
 *     preview may name him and must not hint at it.
 *   - Theron's Carthage story (Evening 7, forty jars of wine) and his Syracuse
 *     story (Evening 6) are not told here. The sixty jars of oil have no
 *     harbour named, on purpose.
 *   - Three unspoken questions, never five (why should I care, can I trust
 *     you, what would you have me do). They live in the Audience Portrait
 *     tool; the scene uses the other three.
 *
 * Every study below was checked against its abstract or record on 2026-10-04;
 * see docs/audits/finding-your-voice-2026-10/register-modules-4-6.md. Nothing
 * was added to them here; they were only shortened.
 */

export const MODULE_04_CONTENT = [
  // --- Through the gate ---
  {
    type: 'narration',
    text: 'Three weeks to the gathering. Xenia is pacing out where the extra benches will go, and you are in your place between Theron and Philemon, when the gate bangs open. A young woman comes through it already talking, a wax tablet held flat against her chest.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'Xenia, I can\'t. My uncle put my name forward and nobody asked me. There will be more people on these stones than live in my street, every one of them looking at me, and my mind will empty, it always does. And Philemon of the Lyceum is speaking the same night. Thirty years a teacher of rhetoric. He will hear every mistake I make.',
  },
  {
    type: 'narration',
    text: 'On your left, Philemon takes a close interest in his cup.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Dreadful man. I hear he eats students.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'I am Philemon. I will be at the front that night, too busy with my own dread to catch a single mistake of yours.',
  },
  {
    type: 'narration',
    text: 'Daria looks at him, then at his cup, which is not quite steady. She has stopped talking. Theron points at the back of her hand, where something is written in ink.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'What does that say?',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'It says "slower".',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Is it working?',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'No.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'Sit, Daria. There, by our guest.',
  },
  {
    type: 'narration',
    text: 'She sits, and gives you the nod of one frightened guest to another.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'You have told me about your empty mind and all those eyes. You have told me about one listener, and you had him wrong. Who else is in this room of yours?',
  },

  // --- One face ---
  {
    type: 'heading',
    text: 'One face',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'Everyone.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'Everyone has no face. Who is one of them?',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'The eel-seller from the third mooring. She comes to everything, and she stands right at the front, like this.',
  },
  {
    type: 'narration',
    text: 'To fold her arms she has to put the tablet down on her knees. She does not seem to notice that she has.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'What does she know about your harbour levy?',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'More than I do. She pays it.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'What does she need?',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'Somebody to say what it costs her. Out loud, in front of the men who set it.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'And what is she afraid of?',
  },
  {
    type: 'narration',
    text: 'Daria opens her mouth to answer at her usual pace, and doesn\'t.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'That whoever stands up will make a mess of it, and nobody will be let near the subject again for years. She isn\'t folding her arms at me. She wants me to be good. Oh. That is worse.',
  },
  {
    type: 'narration',
    text: 'But she has said it at half her speed, and nobody at the table has looked anywhere else. You find you have been leaning in. You could pick the eel-seller out of a crowd.',
  },
  {
    type: 'research',
    title: 'How a listener follows a speaker',
    source: 'Stephens, Silbert and Hasson, PNAS, 2010',
    paragraphs: [
      'In 2010 Greg Stephens, Lauren Silbert and Uri Hasson scanned a woman\'s brain as she told an unrehearsed story, then scanned eleven people listening to the recording. Their brain activity came to follow hers, in most areas one to three seconds behind. In some areas it ran ahead, as if predicting what was coming, and the more it did, the better the understanding.',
      'One speaker and eleven listeners, who never met. The study measured understanding, not any felt sense of connection. On this evidence, though, a listener is taking part.',
    ],
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'That is one. The courtyard will be full, and when I picture all of them at once I want to run.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'So did I, when I first kept this house. I was never afraid of speaking. I was afraid someone would walk home unheard. So before I say a word to any room, I look. Who is at ease. Who is anxious. Who has something to say and cannot find the gap.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'And while you are looking at them, where does the fear go?',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'Attention always has somewhere to live, Daria. If you don\'t give it a room, it will live in you.',
  },
  {
    type: 'exercise',
    number: 1,
    title: 'One listener',
    instructions: 'Do for your own room what Daria has just done for hers. Think of the next time you have to speak, and pick one real person who will be there. If one face worries you, pick that one.',
    prompts: [
      'What do they already know about what you are going to say?',
      'What do they need from you? Information, reassurance, direction, permission?',
      'What are they afraid of? Change, looking foolish, being wrong?',
      'What would make this one person feel heard?',
    ],
  },

  // --- Theron's rooms ---
  {
    type: 'heading',
    text: 'Theron\'s rooms',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'And the men who set the levy? They will be there too, with their arms folded. They are not hoping I will be good.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'There. The girl has more sense than her host. I have stood in front of those arms, Xenia. They were not hoping for anything. They were working out how little they could pay me.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'He is not wrong, Xenia. Of the forty scholars I froze in front of at nineteen, I could name you two who enjoyed it.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'Then some arms mean what Theron says. From where you stand you cannot tell which. So until a face shows me otherwise, I give it the kindest plausible explanation. Not because I am good. Because I speak worse to a room I have already decided to fear.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'The kindest plausible explanation. I\'m going to write that on my other hand.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Write "count your jars" under it. The kindest face I ever sold to took sixty jars of oil off me and paid for thirty.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'One face. Out of how many, in thirty years?',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'That is the one I remember.',
  },
  {
    type: 'narration',
    text: 'Xenia lets him keep it, and turns to you.',
  },
  {
    type: 'yourTurn',
    id: 'm4-last-room',
    character: 'xenia',
    speaker: 'Xenia',
    prompt: 'And you. Think of the last room that frightened you. What had you decided they were thinking?',
    options: [
      {
        label: 'That I had no business being up there.',
        reply: {
          character: 'philemon',
          speaker: 'Philemon',
          text: 'I decided that for years, about rooms that had crossed Athens to hear me. But somebody asked for you, as Daria\'s uncle asked for her. Before your next room, find out who, and what they hoped you would say.',
        },
      },
      {
        label: 'That I was boring them.',
        reply: {
          character: 'xenia',
          speaker: 'Xenia',
          text: 'What did bored look like? Still, quiet, eyes on you? So does listening. Next time, find the stillest face in the room and give it one whole sentence. Then watch what it does.',
        },
      },
      {
        label: 'That they were waiting for me to slip.',
        reply: {
          character: 'theron',
          speaker: 'Theron',
          text: 'Some were. I will not tell you otherwise, whatever Xenia says. But a merchant counts before he panics. Count them next time. In my worst room it was three men in thirty, and I gave those three the whole morning.',
        },
      },
      {
        label: 'I couldn\'t tell you. I never looked at them.',
        reply: {
          character: 'daria',
          speaker: 'Daria',
          text: 'Neither did I. I built the whole room on the walk here and gave every face in it my aunt\'s eyebrows. Find one real person who will be in your next room. I have an eel-seller. You may not have her.',
        },
      },
    ],
  },

  // --- Three weeks ---
  {
    type: 'heading',
    text: 'Three weeks',
  },
  {
    type: 'narration',
    text: 'The lamps have burnt low by the time Daria stands to go. She is carrying the tablet by one corner.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Three weeks, Daria. But who is counting?',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'I am. Twenty-one days. I have written it under the eel-seller.',
  },
  {
    type: 'narration',
    text: 'At the gate she stops under the lamp to write on her other hand. Theron watches her go with his arms folded. You try the kindest plausible explanation. Perhaps he is cold. It is a warm night.',
  },

  {
    type: 'divider',
  },

  // --- To take away ---
  {
    type: 'heading',
    text: 'Before the next evening',
  },
  {
    type: 'exercise',
    number: 2,
    title: 'Three kind readings',
    instructions: 'Practise Xenia\'s habit where nothing rides on it: the next meeting, class or gathering where somebody else is doing the talking.',
    prompts: [
      'Find the face that would worry you most if you were the one speaking: folded arms, a frown, eyes on the floor.',
      'Write three explanations for it that are kind and that you could actually believe. Tired. Cold. Dreading their own turn.',
      'Notice what each one does to how you would speak to that person.',
      'Where you can, check a guess afterwards by asking them how they found it.',
    ],
  },
  {
    type: 'toolLink',
    toolId: 'audience-portrait',
    title: 'Audience Portrait',
    text: 'Xenia\'s tool takes one audience at a time: who they are, what they already know, what they need and what they are afraid of. It adds the three questions we assume no room asks aloud (why should I care, can I trust you, what would you have me do), and keeps each portrait to read again before you speak.',
  },

  // --- The research, for whoever wants it ---
  {
    type: 'notes',
    title: 'What we know now',
    intro: 'Xenia and Theron can only tell you what their own rooms have taught them. Here is what has been tested since.',
    items: [
      {
        title: 'Where the attention goes',
        paragraphs: [
          'In 1995 David Clark and Adrian Wells proposed that socially anxious people, once in front of others, turn their attention inward to check how they are coming across. That inward watch feeds the anxiety, and it leaves little attention for the room, so they miss the signs that people are mostly taking them kindly.',
          'Experiments since then point the same way. In one, run by Sheila Woody at Yale in 1996, 38 people with social phobia did a speech task; those nudged to focus on themselves expected to feel more anxious, and looked it. So we would not promise that your anxiety will go. Our claim is smaller: attention turned on yourself feeds the anxiety, and attention given to your audience gives it less to feed on.',
        ],
        source: 'Clark and Wells, in Social Phobia: Diagnosis, Assessment, and Treatment, 1995; Woody, Journal of Abnormal Psychology, 1996',
      },
      {
        title: 'How a folded arm gets read',
        paragraphs: [
          'People tend to explain what others do by who they are, and to overlook the situation they are in. In a 1967 experiment at Duke University, Edward Jones and Victor Harris had students read an essay for or against Fidel Castro. Even when told that the writer had been assigned a side to argue, readers still took the essay as a sign of the writer\'s real opinion. Lee Ross later named the habit the fundamental attribution error. It is stronger in some cultures than in others. Anxiety makes it worse, because an anxious mind is quicker to put a bad reading on an ambiguous moment.',
          'Choosing the kindest plausible reading of an audience is our advice, not a research finding. From the front of a room you cannot know why someone is frowning, so you may as well pick the reading that keeps you open. None of this shows that every room is friendly, and Theron would tell you that some are not.',
        ],
        source: 'Jones and Harris, Journal of Experimental Social Psychology, 1967',
      },
      {
        title: 'Reading a room can be trained',
        paragraphs: [
          'Many people think of "reading the room" as a gift you either have or lack. In 2012 Danielle Blanch-Hartigan, Susan Andrzejewski and Krista Hill pooled the studies that had tried to teach ordinary adults to judge other people more accurately, and found that training worked. Practice and feedback each did more than instruction alone, and a mix of approaches did best.',
          'So watch, make a guess, and hold the guess loosely. A folded arm has many possible causes.',
        ],
        source: 'Blanch-Hartigan, Andrzejewski and Hill, Basic and Applied Social Psychology, 2012',
      },
      {
        title: 'More from the listening study',
        paragraphs: [
          'When the team repeated the test with a story told in Russian, played to people who spoke no Russian, the effect disappeared. Afterwards each listener was tested on the story. Those whose brain activity followed the speaker\'s more widely tended to have understood it better.',
        ],
        source: 'Stephens, Silbert and Hasson, PNAS, 2010',
      },
    ],
  },

  // --- Module Summary ---
  {
    type: 'summary',
    characterId: 'xenia',
    points: [
      'Before you speak, pick one real listener and ask three things: what do they know, what do they need, what are they afraid of?',
      'From the front of a room you cannot know why a face is frowning. Choose the kindest explanation you can believe.',
      'Attention turned on yourself feeds the anxiety. Attention given to the room gives it less to feed on.',
    ],
    nextPreview: {
      dialogue: [
        {
          character: 'philemon',
          text: 'I have an opening for the gathering. I mean to try it on this table before I dare try it on Eudoros.',
        },
        {
          character: 'theron',
          text: 'Bring the short one.',
        },
      ],
    },
  },
];

export const MODULE_04_EXERCISES = [
  {
    number: 1,
    title: 'One listener',
    description: 'Answer three questions about one real person in your next room.',
    xpReward: 20,
  },
  {
    number: 2,
    title: 'Three kind readings',
    description: 'Practise the kindest plausible reading of a face before you need it.',
    xpReward: 20,
  },
];

export const MODULE_04_SUMMARY = {
  title: "The Listener's Mindset",
  keyInsight: 'A room is only people. Pick one and ask what they need.',
  characterQuote: {
    character: 'xenia',
    text: 'Attention always has somewhere to live. If you don\'t give it a room, it will live in you.',
  },
  takeaways: [
    'Pick one real listener: what do they know, what do they need, what are they afraid of?',
    'You cannot know why a face is frowning. Choose the kindest explanation you can believe.',
    'Attention turned on yourself feeds the anxiety; given to the room, it has less to feed on.',
  ],
};
